/**
 * API-Aufrufe des Wizards „Neue Kampagne“ — Vertrag siehe `campaign-wizard.types.ts`.
 *
 * Jede Funktion gibt die Antwort zurück oder wirft einen `WizardApiError` mit lesbarer Meldung.
 * Die Oberfläche fängt ihn an der Stelle ab, an der sie dem Nutzer etwas sagen kann.
 */

import { API_V1 } from 'API/APIs/APIv1';
import { isCampaignNameTakenError } from '../campaign-builder/campaign-name-taken';
import { parseGeoResponse, parsePostalResponse } from './steps/candidates/candidates-logic';
import {
  AdCheckResult,
  BlacklistTarget,
  BriefingDerivation,
  CandidateFilter,
  CriteriaSet,
  CriteriaTemplate,
  GeoOption,
  Judgement,
  RunSnapshot,
  SearchCriteria,
  SearchEstimate,
  SearchMethod,
  WizardCandidate,
  WizardCandidateDetail,
  WizardCandidateList,
  WizardBlacklistList,
  WizardFlowAnswers,
  WizardLocation,
  WizardSnapshot,
  WizardState,
  WizardStep,
  FlowPlanResult,
  FlowTestStatusResponse,
} from './campaign-wizard.types';

export class WizardApiError extends Error {
  status?: number;
  code?: string;
  nameTaken: boolean;
  raw: unknown;

  constructor(message: string, raw: unknown, status?: number, code?: string) {
    super(message);
    this.name = 'WizardApiError';
    this.raw = raw;
    this.status = status;
    this.code = code;
    this.nameTaken = isCampaignNameTakenError(raw);
  }
}

const BASE = '/campaign-wizard';

/** Der Server meldet Validierungsfehler englisch und knapp („Validation error“): lesbar auf Deutsch ausgeben. */
export function validationMessage(data: any, status?: number): string | null {
  const err = data?.error;
  if (status !== 400 || typeof err !== 'object' || err?.code !== 'VALIDATION_ERROR') return null;
  const paths: string[] = Array.isArray(err.details)
    ? Array.from(
        new Set<string>(
          err.details
            .map((d: { path?: unknown }) => (typeof d?.path === 'string' ? d.path : ''))
            .filter(Boolean)
        )
      )
    : [];
  return paths.length > 0
    ? `Die Eingabe ist ungültig (Feld: ${paths.join(', ')}). Bitte prüfe sie.`
    : 'Die Eingabe ist ungültig. Bitte prüfe sie.';
}

function toError(e: any): WizardApiError {
  const data = e?.response?.data;
  const message: string =
    validationMessage(data, e?.response?.status) ||
    (typeof data?.error === 'object' ? data?.error?.message : undefined) ||
    (typeof data?.error === 'string' ? data.error : undefined) ||
    data?.message ||
    e?.message ||
    'Unbekannter Fehler';
  const code: string | undefined =
    (typeof data?.error === 'object' ? data?.error?.code : undefined) || data?.code;
  return new WizardApiError(message, data ?? e, e?.response?.status, code);
}

async function call<T>(fn: () => Promise<{ data: T }>): Promise<T> {
  try {
    const res = await fn();
    return res.data;
  } catch (e) {
    throw toError(e);
  }
}

/* Ohne Kampagne ---------------------------------------------------- */

/** Zusatzangaben zur Anzeige aus Schritt 1 (Ergänzungen). */
export interface AdExtras {
  extras?: string;
}

export const checkBriefing = (text: string, extras: AdExtras = {}, signal?: AbortSignal) =>
  call<AdCheckResult>(() => API_V1.post(`${BASE}/briefing/check`, { text, ...extras }, { signal }));

export const readBriefingUrl = (url: string) =>
  call<{ text: string; jobTitle?: string }>(() =>
    API_V1.post(`${BASE}/briefing/read-url`, { url })
  );

/** `withIndustries: false` (Namensvorschlag): ohne die große Branchenliste im KI-Prompt. */
export const deriveBriefing = (text: string, extras: AdExtras = {}, withIndustries = true) =>
  call<BriefingDerivation>(() =>
    API_V1.post(`${BASE}/briefing/derive`, { text, ...extras, ...(withIndustries ? {} : { withIndustries: false }) })
  );

export const getCriteriaTemplates = () =>
  call<{ templates: CriteriaTemplate[] }>(() => API_V1.get(`${BASE}/criteria-templates`));

export const saveCriteriaTemplate = (
  name: string,
  criteria: CriteriaSet,
  scope: 'team' | 'own' = 'team'
) =>
  call<CriteriaTemplate>(() =>
    API_V1.post(`${BASE}/criteria-templates`, { name, criteria, scope })
  );

export const criteriaFromBriefing = (text: string) =>
  call<{ criteria: CriteriaSet; source: 'ai' | 'heuristic' }>(() =>
    API_V1.post(`${BASE}/criteria-from-briefing`, { text })
  );

export const getWizardBlacklists = () =>
  call<{ lists: WizardBlacklistList[] }>(() => API_V1.get(`${BASE}/blacklists`));

export const importWizardBlacklist = (
  name: string,
  type: 'company' | 'contact',
  entries: string[]
) =>
  call<WizardBlacklistList>(() =>
    API_V1.post(`${BASE}/blacklists/import`, { name, type, entries })
  );

/**
 * LinkedIn-Ortssuche (Autovervollständigung): Konto = das im Wizard gewählte Absender-Konto.
 * Antwort des Endpunkts: `[[id, text], …]`. Abgelaufene Sitzung = 409 (oder 400) mit `code: 'SESSION_INVALID'`
 * → `WizardApiError` mit `status` / `code`, die Oberfläche unterscheidet das mit `geoErrorKind`.
 */
export const lookupGeoLocations = async (
  accountId: string,
  query: string,
  signal?: AbortSignal,
  lang = 'de'
): Promise<GeoOption[]> => {
  const data = await call<unknown>(() =>
    API_V1.post('/linkedin/lookups/geo', { linkedinAccount: accountId }, { params: { query, lang }, signal })
  );
  return parseGeoResponse(data);
};

/**
 * PLZ-Suche für „PLZ + Umkreis“ (Sales Navigator, Recruiter): beide Endpunkte prüfen das Konto gegen den Mandanten.
 * Sales Navigator `POST /linkedin/lookups/geo/postal-code`, Recruiter `POST /linkedin/lookups/recruiter-constraints?searchType=zip`.
 * Fehler wie bei `lookupGeoLocations` (`WizardApiError` mit `status` / `code`).
 */
export const lookupPostalCodes = async (
  accountId: string,
  query: string,
  productType: string | null | undefined,
  signal?: AbortSignal,
  lang = 'de'
): Promise<GeoOption[]> => {
  const data = await call<unknown>(() =>
    productType === 'recruiter'
      ? API_V1.post(
          '/linkedin/lookups/recruiter-constraints',
          { linkedinAccount: accountId },
          { params: { searchType: 'zip', query, lang }, signal }
        )
      : API_V1.post(
          '/linkedin/lookups/geo/postal-code',
          { postalCode: query, linkedinAccount: accountId },
          { signal }
        )
  );
  return parsePostalResponse(data, productType);
};

export const createWizardCampaign = (input: {
  name: string;
  accountId: string;
  briefing: string;
  briefingExtras?: string;
  cloneFromCampaignId?: string;
  cloneContacts?: boolean;
}) =>
  call<{ campaignId: string; state: WizardState }>(() => API_V1.post(`${BASE}/campaigns`, input));

/* Mit Kampagne ----------------------------------------------------- */

export const getWizard = (id: string) => call<WizardSnapshot>(() => API_V1.get(`${BASE}/${id}`));

export const saveWizardState = (
  id: string,
  state: WizardState,
  step: WizardStep,
  signal?: AbortSignal
) => call<WizardSnapshot>(() => API_V1.put(`${BASE}/${id}/state`, { state, step }, { signal }));

export const estimateSearch = (
  id: string,
  body: {
    locations: WizardLocation[];
    criteria: SearchCriteria;
    method: SearchMethod;
    searchUrl?: string;
  },
  signal?: AbortSignal
) => call<SearchEstimate>(() => API_V1.post(`${BASE}/${id}/search/estimate`, body, { signal }));

export const startSearch = (id: string) =>
  call<{ started: boolean; message?: string }>(() => API_V1.post(`${BASE}/${id}/search/start`));

/** „Nächste 50 laden“: weitere Profile über die am Ziel angehaltenen Suchen. */
export const loadMoreCandidates = (id: string) =>
  call<{ started: boolean; requested: number; message?: string }>(() =>
    API_V1.post(`${BASE}/${id}/candidates/load-more`)
  );

export const getCandidates = (
  id: string,
  params: { filter?: CandidateFilter; judgement?: Judgement[] } = {}
) =>
  call<WizardCandidateList>(() =>
    API_V1.get(`${BASE}/${id}/candidates`, {
      params: {
        filter: params.filter,
        judgement:
          params.judgement && params.judgement.length > 0 ? params.judgement.join(',') : undefined,
      },
    })
  );

export const getCandidate = (id: string, cid: string) =>
  call<WizardCandidateDetail>(() => API_V1.get(`${BASE}/${id}/candidates/${cid}`));

export const patchCandidate = (
  id: string,
  cid: string,
  body: { active?: boolean; judgement?: Judgement | null }
) => call<WizardCandidate>(() => API_V1.patch(`${BASE}/${id}/candidates/${cid}`, body));

/** Geschlecht eines Kontakts festlegen (unklares Geschlecht, Schritt 4) – derselbe Weg wie in der alten Pipeline. */
export const setContactSex = (contactId: string, sex: 'female' | 'male') =>
  call<unknown>(() => API_V1.patch(`/contacts/${contactId}`, { sex }));

/** „Erneut bewerten“ eines fehlgeschlagenen oder wartenden Kandidaten. */
export const rejudgeCandidate = (id: string, cid: string) =>
  call<WizardCandidate>(() => API_V1.post(`${BASE}/${id}/candidates/${cid}/rejudge`));

export const blacklistCandidate = (
  id: string,
  cid: string,
  scope: 'person' | 'company',
  target: string
) =>
  call<WizardCandidateList>(() =>
    API_V1.post(`${BASE}/${id}/candidates/${cid}/blacklist`, { scope, target })
  );

export const unblacklistCandidate = (id: string, cid: string, scope: 'person' | 'company') =>
  call<WizardCandidateList>(() =>
    API_V1.delete(`${BASE}/${id}/candidates/${cid}/blacklist`, { params: { scope } })
  );

/** Menü „… hinzufügen zu“: Ziele werden im Frontend aus den Blacklists zusammengesetzt (siehe `blacklistTargets`). */
export type { BlacklistTarget };

export const planFlow = (id: string, answers: WizardFlowAnswers, signal?: AbortSignal) =>
  call<FlowPlanResult>(() => API_V1.post(`${BASE}/${id}/flow/plan`, { answers }, { signal }));

/** Startet den Test des kompletten gespeicherten Ablaufs; antwortet mit dem Ergebnis oder `running` (dann `getFlowTest`). */
export const startFlowTest = (id: string, candidateId: string) =>
  call<FlowTestStatusResponse>(() => API_V1.post(`${BASE}/${id}/flow/test`, { candidateId }));

export const getFlowTest = (id: string, candidateId: string, testId: string) =>
  call<FlowTestStatusResponse>(() =>
    API_V1.get(`${BASE}/${id}/flow/test`, { params: { candidateId, testId } })
  );

export const startWizardCampaign = (id: string) =>
  call<RunSnapshot>(() => API_V1.post(`${BASE}/${id}/start`));

export const getRun = (id: string) => call<RunSnapshot>(() => API_V1.get(`${BASE}/${id}/run`));
